import { Box, Chip, Paper, Skeleton, Stack, Typography } from '@mui/material';
import { useForm } from 'react-hook-form';
import { IoCalendarClearOutline } from 'react-icons/io5';
import { RxCross2 } from 'react-icons/rx';
import AppliedCouponIcon from 'assets/icons/AppliedCouponIcon';
import Input from 'components/Common/Input';
import { useAddCoupon, useRemoveCoupon } from '@/apis/business/setting/billing/hook';
import { CacheKeys } from '@/utils/cacheKeys';
import { formatDollars } from '@/utils/helperFunctions';
import Button from '@/components/Common/Button';
import WarningIcon from '@/assets/icons/WarningIcon';

const ApplyCoupon = ({ appliedCouponDetails, isLoading }: any) => {
  let businessIds: number[];
  if (localStorage.getItem(CacheKeys.businessIds)) {
    businessIds = JSON.parse(localStorage.getItem(CacheKeys.businessIds) ?? '');
  }
  const { mutate: addCoupon, isPending: addingCoupon } = useAddCoupon();
  const { mutate: removeCoupon, isPending: removingCoupon } = useRemoveCoupon();
  const { handleSubmit, control } = useForm();
  const isExpired = !appliedCouponDetails?.is_active;

  const couponTheme = isExpired
    ? {
        border: '#D32F2F',
        bg: '#FFF4F4',
        text: '#D32F2F',
        subText: '#DA0808',
        chipBg: '#FDEAEA',
        chipBorder: '#DA0808',
        icon: <WarningIcon />
      }
    : {
        border: '#077F13',
        bg: '#F4FFF5',
        text: '#2E7D32',
        subText: '#2E7D32',
        chipBg: '#DBFCDE',
        chipBorder: '#077F13',
        icon: <AppliedCouponIcon />
      };

  const onSubmit = async (data: any) => {
    data = { ...data, business_id: businessIds?.[0] };
    await addCoupon(data);
  };

  if (isLoading) return <ApplyCouponSectionSkeleton />;

  return (
    <Paper variant='outlined' sx={{ p: { xs: 1.5, lg: 2 }, mb: 3, borderRadius: 2 }}>
      <Stack spacing={2}>
        {/* Static Header */}
        <Box>
          <Typography variant='subtitle1' fontWeight={500} fontSize={20}>
            Apply Coupon
          </Typography>
          <Typography
            variant='body2'
            color='text.secondary'
            fontSize={16}
            mt={0.5}
            sx={{ display: { xs: 'none', sm: 'block' } }}
          >
            Enter a coupon code to get discounts on your subscription
          </Typography>
        </Box>

        {/* Conditional Rendering */}
        {appliedCouponDetails?.code ? (
          <Box
            sx={{
              border: `1px solid ${couponTheme.border}`,
              borderRadius: 1,
              bgcolor: couponTheme.bg,
              p: { xs: 1.5, lg: 2 },
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' },
              justifyContent: 'space-between',
              width: '100%',
              gap: { xs: 2, sm: 0 }
            }}
          >
            {/* Left Section */}
            <Stack
              direction='row'
              spacing={2}
              alignItems='center'
              flex={1}
              width={{ xs: '100%', sm: 'auto' }}
              flexWrap='wrap'
            >
              {couponTheme.icon}
              <Stack spacing={0.5} flex={1} minWidth={0}>
                <Stack
                  direction='row'
                  alignItems='center'
                  spacing={1}
                  flexWrap='wrap'
                  sx={{ flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' } }}
                >
                  <Typography fontSize={20} sx={{ fontWeight: 500, color: couponTheme.chipBorder }}>
                    {appliedCouponDetails.name}
                  </Typography>
                  <Chip
                    label={
                      appliedCouponDetails.discount_type === 'percentage'
                        ? appliedCouponDetails.total_amount != null && Number(appliedCouponDetails.total_amount) > 0
                          ? `${appliedCouponDetails.discount_value}% Off up to ${formatDollars(appliedCouponDetails.total_amount)}`
                          : `${appliedCouponDetails.discount_value}% Off`
                        : `${formatDollars(appliedCouponDetails.discount_value)} Off`
                    }
                    size='small'
                    sx={{
                      bgcolor: couponTheme.chipBg,
                      borderRadius: '34px',
                      border: `1px solid ${couponTheme.border}`,
                      color: couponTheme.subText,
                      fontWeight: 500,
                      height: 24
                    }}
                  />
                </Stack>

                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={{ xs: 1, sm: 3 }}
                  alignItems={{ xs: 'flex-start', sm: 'center' }}
                  flexWrap='wrap'
                >
                  <Typography variant='body2' sx={{ color: couponTheme.subText }}>
                    Code: <strong>{appliedCouponDetails.code}</strong>
                  </Typography>

                  <Stack direction='row' spacing={0.5} alignItems='center'>
                    <IoCalendarClearOutline color={couponTheme.subText} size={14} />
                    <Typography variant='body2' fontWeight={500} sx={{ color: couponTheme.subText }}>
                      Applied: {appliedCouponDetails.applied_on}
                    </Typography>
                  </Stack>

                  <Stack direction='row' spacing={0.5} alignItems='center'>
                    <IoCalendarClearOutline color={couponTheme.subText} size={14} />
                    <Typography variant='body2' fontWeight={500} sx={{ color: couponTheme.subText }}>
                      Valid Until: {appliedCouponDetails.valid_until}
                    </Typography>
                  </Stack>
                </Stack>
              </Stack>
            </Stack>

            {/* Right Remove Button */}
            <Button
              variant='outlined'
              size='small'
              disabled={removingCoupon}
              startIcon={<RxCross2 />}
              onClick={() => removeCoupon({ business_id: businessIds?.[0], code: appliedCouponDetails?.code })}
              sx={{
                borderColor: '#E0E0E0',
                color: '#6E6E6E',
                borderRadius: 8,
                px: 2,
                textTransform: 'none',
                bgcolor: 'white',
                width: { xs: '100%', sm: 'auto' },
                '&:hover': { bgcolor: '#f5f5f5', borderColor: '#ccc' }
              }}
            >
              Remove
            </Button>
          </Box>
        ) : (
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            gap={{ xs: 2, sm: 4 }}
            justifyContent='space-between'
            component='form'
            onSubmit={handleSubmit(onSubmit)}
            alignItems={{ xs: 'stretch', sm: 'center' }}
          >
            <Input name='code' control={control} label='Coupon Code' fullWidth />
            <Box m={{ xs: 0, sm: 'auto' }}>
              <Button
                variant='contained'
                type='submit'
                size='small'
                isPending={addingCoupon}
                sx={{ borderRadius: 5, whiteSpace: 'nowrap', width: { xs: '100%', sm: 'auto' } }}
              >
                Apply Coupon
              </Button>
            </Box>
          </Stack>
        )}
      </Stack>
    </Paper>
  );
};

export default ApplyCoupon;

// skelton

const ApplyCouponSectionSkeleton = () => {
  return (
    <Paper variant='outlined' sx={{ p: { xs: 1.5, lg: 2 }, mb: 3, borderRadius: 2 }}>
      <Stack spacing={2}>
        {/* Static Heading */}
        <Box>
          <Typography variant='subtitle1' fontWeight={500} fontSize={20}>
            Apply Coupon
          </Typography>
          <Typography
            variant='body2'
            color='text.secondary'
            fontSize={16}
            mt={0.5}
            sx={{ display: { xs: 'none', sm: 'block' } }}
          >
            Enter a coupon code to get discounts on your subscription
          </Typography>
        </Box>

        {/* Skeleton Card for Applied Coupon */}
        <Box
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            p: { xs: 1.5, lg: 2 },
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'flex-start', sm: 'center' },
            justifyContent: 'space-between',
            width: '100%',
            gap: { xs: 2, sm: 0 }
          }}
        >
          {/* Left Section */}
          <Stack direction='row' spacing={2} alignItems='center' width={{ xs: '100%', sm: 'auto' }} flex={1}>
            <Skeleton variant='circular' width={36} height={36} />
            <Stack spacing={1} width='100%' flex={1}>
              <Skeleton variant='text' height={24} sx={{ width: { xs: '60%', sm: '40%' } }} />
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={{ xs: 1, sm: 2 }}
                alignItems={{ xs: 'flex-start', sm: 'center' }}
              >
                <Skeleton variant='text' height={20} sx={{ width: { xs: '100%', sm: '30%' } }} />
                <Skeleton variant='text' height={20} sx={{ width: { xs: '100%', sm: '30%' } }} />
                <Skeleton variant='text' height={20} sx={{ width: { xs: '100%', sm: '30%' } }} />
              </Stack>
            </Stack>
          </Stack>

          {/* Right Remove Button */}
          <Skeleton variant='rounded' height={36} sx={{ width: { xs: '100%', sm: 90 } }} />
        </Box>
      </Stack>
    </Paper>
  );
};
